import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';

// Skeleton para gráficos de barras horizontais
export const HorizontalBarChartSkeleton: React.FC<{ height?: number; showTitle?: boolean; showTable?: boolean }> = ({ 
  height = 400, 
  showTitle = true, 
  showTable = true 
}) => {
  const theme = useTheme();
  
  const chartHeight = showTable ? height * 0.6 : height - (showTitle ? 50 : 20);
  const tableHeight = showTable ? height * 0.3 : 0;
  
  return (
    <Box sx={{ overflow: 'hidden', width: '100%', height }}>
      {/* Título opcional */}
      {showTitle && (
        <Skeleton 
          variant="text" 
          sx={{ fontSize: '1rem', mb: 2, mx: 'auto', width: '70%' }} 
        />
      )}
      
      {/* Área do gráfico */}
      <Box sx={{ height: chartHeight, position: 'relative', overflow: 'hidden' }}>
        {/* Eixos */}
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 40, width: 80 }}>
          {Array.from({ length: Math.min(8, Math.floor(chartHeight / 40)) }).map((_, index) => (
            <Skeleton 
              key={index}
              variant="text" 
              sx={{ 
                width: '60px', 
                height: '14px',
                mb: 2,
                borderRadius: 1
              }} 
            />
          ))}
        </Box>
        
        {/* Barras */}
        <Box sx={{ ml: 10, mr: 2, height: chartHeight - 40, display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
          {Array.from({ length: Math.min(8, Math.floor(chartHeight / 40)) }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Skeleton 
                variant="rounded" 
                sx={{ 
                  width: `${Math.random() * 60 + 20}%`, 
                  height: Math.max(20, chartHeight / 20),
                  borderRadius: 1
                }} 
              />
            </Box>
          ))}
        </Box>
        
        {/* Eixo inferior */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 80, right: 0, height: 30, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="text" sx={{ width: '40px', height: '12px' }} />
          ))}
        </Box>
      </Box>
      
      {/* Tabela de dados restantes (quando existir) */}
      {showTable && tableHeight > 0 && (
        <Box sx={{ mt: 2, height: tableHeight }}>
          <Skeleton variant="text" sx={{ fontSize: '0.9rem', mb: 1, mx: 'auto', width: '50%' }} />
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1, height: tableHeight - 30, overflow: 'hidden' }}>
            {Array.from({ length: Math.min(3, Math.floor((tableHeight - 30) / 35)) }).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: index < 2 ? `1px solid ${theme.palette.divider}` : 'none' }}>
                <Skeleton variant="text" sx={{ width: '60%' }} />
                <Skeleton variant="text" sx={{ width: '20%' }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Skeleton para gráficos de barras verticais
export const VerticalBarChartSkeleton: React.FC<{ height?: number; showTitle?: boolean }> = ({ height = 740, showTitle = true }) => {
  return (
    <Box sx={{ overflow: 'hidden', width: '100%', height }}>
      {/* Título opcional */}
      {showTitle && (
        <Skeleton 
          variant="text" 
          sx={{ fontSize: '1rem', mb: 2, mx: 'auto', width: '70%' }} 
        />
      )}
      
      {/* Área do gráfico */}
      <Box sx={{ height: showTitle ? height - 50 : height, position: 'relative', overflow: 'hidden' }}>
        {/* Eixo esquerdo */}
        <Box sx={{ position: 'absolute', left: 0, top: 20, bottom: 80, width: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} variant="text" sx={{ width: '60px', height: '12px' }} />
          ))}
        </Box>
        
        {/* Barras */}
        <Box sx={{ ml: 10, mr: 2, height: '60%', display: 'flex', alignItems: 'end', justifyContent: 'center', gap: 4, mt: 4 }}>
          <Skeleton variant="rounded" sx={{ width: 120, height: `${Math.random() * 80 + 40}%`, borderRadius: 1 }} />
          <Skeleton variant="rounded" sx={{ width: 120, height: `${Math.random() * 80 + 40}%`, borderRadius: 1 }} />
        </Box>
        
        {/* Eixo inferior */}
        <Box sx={{ position: 'absolute', bottom: 40, left: 80, right: 20, height: 30, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
          <Skeleton variant="text" sx={{ width: '120px', height: '14px' }} />
          <Skeleton variant="text" sx={{ width: '120px', height: '14px' }} />
        </Box>
        
        {/* Legenda */}
        <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" sx={{ width: 12, height: 12 }} />
            <Skeleton variant="text" sx={{ width: '80px', height: '12px' }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" sx={{ width: 12, height: 12 }} />
            <Skeleton variant="text" sx={{ width: '80px', height: '12px' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Skeleton para tabelas hierárquicas
export const HierarchicalTableSkeleton: React.FC<{ height?: number; showTitle?: boolean }> = ({ 
  height = 420, 
  showTitle = true 
}) => {
  const theme = useTheme();
  
  const tableHeight = showTitle ? height - 50 : height;
  const maxRows = Math.floor((tableHeight - 60) / 45);
  
  return (
    <Box sx={{ overflow: 'hidden', width: '100%', height }}>
      {/* Título opcional */}
      {showTitle && (
        <Skeleton 
          variant="text" 
          sx={{ fontSize: '1rem', mb: 2, mx: 'auto', width: '70%' }} 
        />
      )}
      
      {/* Container da tabela */}
      <Box sx={{ 
        border: `1px solid ${theme.palette.divider}`, 
        borderRadius: 2, 
        height: tableHeight,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          backgroundColor: theme.palette.action.hover, 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Skeleton variant="text" sx={{ width: '40%', height: '16px' }} />
          <Skeleton variant="text" sx={{ width: '20%', height: '16px' }} />
        </Box>
        
        {/* Linhas da tabela */}
        <Box sx={{ overflow: 'hidden', height: tableHeight - 60 }}>
          {Array.from({ length: Math.min(maxRows, 8) }).map((_, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 1.5, 
                px: 2,
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                borderBottom: `1px solid ${theme.palette.divider}`,
                height: 40
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '70%' }}>
                <Skeleton variant="rectangular" sx={{ width: 20, height: 20, borderRadius: 0.5 }} />
                <Skeleton variant="text" sx={{ width: `${Math.random() * 40 + 40}%`, height: '16px' }} />
              </Box>
              <Skeleton variant="text" sx={{ width: '20%', height: '16px' }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Skeleton para gráficos de linha/evolução
export const LineChartSkeleton: React.FC<{ height?: number; showTitle?: boolean }> = ({ height = 300, showTitle = true }) => {
  return (
    <Box sx={{ overflow: 'hidden', width: '100%' }}>
      {/* Título opcional */}
      {showTitle && (
        <Skeleton 
          variant="text" 
          sx={{ fontSize: '1rem', mb: 2, mx: 'auto', width: '60%' }} 
        />
      )}
      
      {/* Área do gráfico */}
      <Box sx={{ 
        height: showTitle ? height - 50 : height, 
        position: 'relative', 
        overflow: 'hidden',
        width: '100%'
      }}>
        {/* Eixo esquerdo */}
        <Box sx={{ 
          position: 'absolute', 
          left: 0, 
          top: 20, 
          bottom: 50, 
          width: 60, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between' 
        }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="text" sx={{ width: '50px', height: '10px' }} />
          ))}
        </Box>
        
        {/* Área das barras */}
        <Box sx={{ 
          ml: 8, 
          mr: 2, 
          height: '80%', 
          display: 'flex', 
          alignItems: 'end', 
          justifyContent: 'center', 
          gap: 1,
          pb: 6
        }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton 
              key={index}
              variant="rectangular" 
              sx={{ 
                width: `${100 / 4 - 4}%`,
                height: `${Math.random() * 60 + 30}%`, 
                borderRadius: 0.5,
                maxWidth: 60
              }} 
            />
          ))}
        </Box>
        
        {/* Eixo inferior */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: 10, 
          left: 60, 
          right: 20, 
          height: 30, 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'center' 
        }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="text" sx={{ width: '40px', height: '10px' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Skeleton para gráficos de donut
export const DonutChartSkeleton: React.FC<{ height?: number; showTitle?: boolean }> = ({ height = 400, showTitle = true }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ overflow: 'hidden', width: '100%', height }}>
      {/* Título opcional */}
      {showTitle && (
        <Skeleton 
          variant="text" 
          sx={{ fontSize: '1rem', mb: 2, mx: 'auto', width: '60%' }} 
        />
      )}
      
      {/* Container do gráfico */}
      <Box sx={{ 
        height: showTitle ? height - 50 : height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Donut principal centralizado */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Skeleton 
            variant="circular" 
            sx={{ 
              width: Math.min((showTitle ? height - 50 : height) * 0.55, 240), 
              height: Math.min((showTitle ? height - 50 : height) * 0.55, 240)
            }} 
          />
        </Box>
      </Box>
    </Box>
  );
};
